import Vue from 'vue';
import { $t } from '@/i18n';
import { getAIPageContextInstance } from '@/utils/global/api';
import getXyHeaders from '@/utils/getXyHeaders';
import { message } from 'tdesign-vue';
import { delay } from '@/utils/delay';
import getXyManagePathPrefix from '@/utils/getXyManagePathPrefix';
import { addPathPrefix } from '@/utils/path';
import { clamp, memoize } from 'lodash';
let wujiFetchAccessPromise = null;
export function resetWujiFetchAccessPromise() {
    wujiFetchAccessPromise = null;
}
export async function getWujiFetchAccess() {
    const w = getAIPageContextInstance();
    const { env, projectId } = w;
    const isDev = env === 'dev';
    // 特殊检查
    const hashFromGogoEditor = window.__gogoDebugHijack?.backendHash;
    if (hashFromGogoEditor && wujiFetchAccessPromise?.result && wujiFetchAccessPromise.result.hash !== hashFromGogoEditor) {
        // 过期了
        wujiFetchAccessPromise = null;
    }
    // 获取跨服务调用的 token
    if (!wujiFetchAccessPromise) {
        const clear = () => {
            if (wujiFetchAccessPromise === promise)
                wujiFetchAccessPromise = null;
        };
        // 10分钟后强制重取token
        const promise = (async () => {
            const cleanups = [];
            const deadline = Date.now() + 20e3; // 特殊情况可以一直重试直到 20s
            let hitDeploying = false;
            let answer = null;
            try {
                while (Date.now() < deadline) {
                    const url = `${location.origin}${addPathPrefix(getXyManagePathPrefix())}/ai-project/access?projectId=${projectId}`;
                    const access = await fetch(url, { headers: getXyHeaders() }).then(res => res.json())
                        .then(X => X?.data);
                    // 正在部署ing，可以重试
                    if (access?.deployStatus === 'deploying') {
                        if (!hitDeploying) {
                            hitDeploying = true;
                            if (isDev) {
                                // 开发环境可以显示一个正在部署的提示
                                message.loading($t('k_8f78fe2d')).then((loading) => {
                                    cleanups.push(() => loading.close());
                                });
                            }
                        }
                        await delay(200);
                        continue;
                    }
                    // 部署失败
                    if (access?.deployStatus === 'failed') {
                        const msg = $t('k_5ddc9a0e');
                        message.error(msg);
                        throw new Error(msg);
                    }
                    // 开发模式专属：gogo editor 改了代码但是没部署
                    if (isDev && hashFromGogoEditor && (access?.hash !== hashFromGogoEditor)) {
                        const promise = import('./WaitBackendReadyLayout.vue')
                            .then(m => m.default)
                            .then(Modal => new Promise((res) => {
                            const container = document.createElement('div');
                            document.body.appendChild(container);
                            const vm = new Vue({
                                render: h => h(Modal, {
                                    props: {
                                        projectId,
                                        expectedHash: hashFromGogoEditor,
                                        autoPolling: true,
                                    },
                                    on: {
                                        answer(ans, accessResult) {
                                            res({ ans, accessResult });
                                            setTimeout(() => {
                                                vm.$el.remove();
                                                vm.$destroy();
                                                container.remove();
                                            }, 0);
                                        },
                                    },
                                }),
                            });
                            vm.$mount(container);
                        }));
                        const { ans, accessResult } = await promise;
                        if (ans !== 'request-anyway') {
                            // 用户选择忽略，直接抛出错误
                            throw new Error('User cancelled the request');
                        }
                        // ans === 'request-anyway'，弹窗已经确认部署完成
                        // 如果弹窗返回了最新的 access result，直接使用它
                        if (accessResult?.token) {
                            answer = accessResult;
                            break;
                        }
                        // 继续执行，使用当前的 access
                    }
                    if (!access?.token)
                        throw new Error('Failed to get wujiFetch access');
                    answer = access;
                    break;
                }
                setTimeout(() => {
                    promise.result = answer;
                }, 0);
                return answer;
            }
            catch (err) {
                throw err;
            }
            finally {
                cleanups.forEach(c => c());
                if (!answer?.expiresAt)
                    clear(); // failed to fetch, just do not cache
                else
                    setTimeout(clear, clamp(answer.expiresAt - Date.now(), 1000, 600e3));
            }
        })();
        wujiFetchAccessPromise = promise;
    }
    const access = await wujiFetchAccessPromise;
    if (!access)
        throw new Error('Failed to get wujiFetch access');
    return access;
}
export const doubleCheckLoginStatusAndDisplayLoginIfNeeded = memoize(async () => {
    try {
        const { ssoCheckUrl } = (await getWujiFetchAccess());
        if (!ssoCheckUrl)
            return; // 没有 SSO 检查 URL，直接返回
        const healthCheckRes = await fetch(`${ssoCheckUrl}/health`, {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        });
        if (healthCheckRes.status === 200)
            return; // 健康
        // 不健康，需要重新登录。
        const iframeUrl = `${ssoCheckUrl}/login/`;
        // 创建 wrapper 和 iframe
        const wrapper = document.createElement('div');
        wrapper.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
    `;
        const closeButton = document.createElement('button');
        closeButton.innerHTML = '×';
        closeButton.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.9);
      color: #333;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;
    `;
        closeButton.onmouseover = () => {
            closeButton.style.backgroundColor = 'rgba(255, 255, 255, 1)';
            closeButton.style.transform = 'scale(1.1)';
        };
        closeButton.onmouseout = () => {
            closeButton.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
            closeButton.style.transform = 'scale(1)';
        };
        const contentContainer = document.createElement('div');
        contentContainer.style.cssText = `
      position: relative;
      width: 600px;
      height: 400px;
    `;
        const iframe = document.createElement('iframe');
        iframe.src = iframeUrl;
        iframe.style.cssText = `
      width: 100%;
      height: 100%;
      border: none;
      border-radius: 8px;
      background-color: white;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;
        contentContainer.appendChild(iframe);
        contentContainer.appendChild(closeButton);
        wrapper.appendChild(contentContainer);
        document.body.appendChild(wrapper);
        // 等待登录成功的消息
        return await new Promise((resolve) => {
            const handleMessage = (event) => {
                if (event.data?.type === 'sso-login-ok'
                    && event.data?.source === 'gogo-scf') {
                    // 登录成功，清理 UI
                    window.removeEventListener('message', handleMessage);
                    document.body.removeChild(wrapper);
                    finalize();
                    resolve('sso-login-done');
                    message.success($t('k_2d2025c4'));
                }
            };
            const finalize = () => {
                window.removeEventListener('message', handleMessage);
                if (document.body.contains(wrapper)) {
                    document.body.removeChild(wrapper);
                }
            };
            const abort = () => {
                finalize();
                resolve('aborted');
            };
            // 绑定关闭按钮点击事件
            closeButton.addEventListener('click', abort);
            // 点击遮罩层关闭（点击 wrapper 但不是 contentContainer）
            wrapper.addEventListener('click', (e) => {
                if (e.target === wrapper) {
                    abort();
                }
            });
            window.addEventListener('message', handleMessage);
        });
    }
    finally {
        doubleCheckLoginStatusAndDisplayLoginIfNeeded.cache.clear?.();
    }
}, () => 'doubleCheckLoginStatusAndDisplayLoginIfNeeded');
